import { type ReactNode, useMemo } from "react";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { MAX_QUANTITY } from "~/utils/constants";
import { Link } from "~/common/components/Link";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";

import { Box, Card, Grid, Stack, styled } from "~/common/mui";
import { PackagingSelection } from "~/domains/product/components/PackagingSelection/PackagingSelection";
import CenterContents from "~/common/components/CenterContents";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import {
  getCountriesListFromFifteendigit,
  getPriceInfo,
  isProductPMA,
} from "~/domains/product/utils/productDescriptorUtils";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { useSelector } from "react-redux";
import { selectIsCartAmountsIsLoading } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import DistributorPriceTable from "~/features/productDetails/components/AddToCartCard/PurchaserAddToCartCard/DistributorPriceTable";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { trackPdpWishlistClick } from "~/app/analytics/ga/ga";
import DisclaimerCountryOrigin from "~/domains/product/components/DisclaimerCountryOrigin";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import { isKITKITProduct, isKITProduct, type PackagingInformationEntry } from "~/domains/product/types/ProductData";
import { Controller, useFormContext } from "react-hook-form";
import type { AddToCartFormDraft, OptionalPartDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useAddToCartDerived } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import { usePackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { HousingMessages } from "~/domains/housing/components/HousingMessages";
import {
  findMinOrderQuantity,
  getDefaultQuantity,
  shouldShowPriceReference,
  usePdpSummaryLogic,
} from "~/features/productDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { Trans, useTranslation } from "react-i18next";
import {
  PhaseInOutLogicProvider,
  usePhaseInOutLogic,
} from "~/features/productDetails/components/PhaseInOut/usePhaseInOutLogic";
import { SuccessorNotification } from "~/features/productDetails/components/PhaseInOut/SuccessorNotification/SuccessorNotification";
import { DiscontinuationNotification } from "~/features/productDetails/components/PhaseInOut/DiscontinuationNotification/DiscontinuationNotification";
import { resolveUrl } from "~/utils/url/urlResolver";
import { getTotalStock } from "~/utils/packageEntryUtils";
import { AlternativeNotification } from "~/features/productDetails/components/PhaseInOut/AlternativeNotification/AlternativeNotification";
import {
  PHASE_IN_BUY_ERROR_KEY,
  PHASE_IN_BUY_WARN_KEY,
} from "~/features/productDetails/components/PhaseInOut/constants";
import { PreferredProducts } from "~/features/productDetails/components/PhaseInOut/PreferredProducts/PreferredProducts";
import { ProductContext, ProductDescriptorContext } from "~/domains/product/ProductContext";
import { calculateSalesCampaign4PDP, type PdpSaleData } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { SalesDataProvider } from "~/features/salesCampaign/hooks/useSalesData";
import { SaleEndIndicator } from "~/domains/salesCampaign/components/SaleEndIndicator/SaleEndIndicator";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import {
  getPurchaseDataFromUrl,
  selectAmountInCart,
} from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import { Alert } from "~/common/mui/Alert/Alert";
import { InfoOrderHandlingUnitQuantity } from "~/features/productDetails/components/ProductSummary/InfoOrderHandlingUnitQuantity";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { renderButtons } from "~/features/productDetails/components/ProductSummary/components/Buttons/usePdpButtons";
import { TabId } from "~/features/productDetails/components/usePDPTabs";
import { isMinOrderQuantityRestrictionEnabled, isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import MMDAssistant from "~/features/productDetails/components/MMDAssistant/MMDAssistant";
import { useDisplayMMDAssistant } from "~/features/productDetails/context/DisplayMMDAssistantContext";
import { usePdpCmnLogic } from "~/features/productDetails/components/MMDAssistant/logic/usePdpCmnLogic";
import {
  AddServiceProductsSection,
  useAddServiceProductsDataState,
} from "~/features/productDetails/components/AddServiceProductsSection/AddServiceProductsSection";
import { Product } from "~/domains/product/Product";
import { usePackagingOptions } from "~/domains/product/hooks/usePackagingOptions";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { useSimulateCartPreview } from "~/domains/cart/hooks";
import { ProductAddPreview } from "~/domains/product/components/ProductAddPreview";
import { PdpPriceBlock } from "./components/PdpPriceBlock";
import { VariantSelect } from "./components/VariantSelect";
import { useVariantContext } from "../../context/VariantContext";
import { ProductDetailsSection } from "./components/ProductDetailsSection";
import { addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";
import {
  addToCartItemFromSubmitData,
  addToCartItemsFromBasicItems,
} from "~/features/addToCart/redux/addToCart/addToCartItemFactory";
import { KAMELEOON_FLAGS, useKameleoonFlag } from "~/common/config/kameleoonFlags";

const PackagingArea = styled("div")(({ theme }) => ({
  background: theme.palette.surface.main,
  display: "flex",
  flexFlow: "column",
  flex: 1,
  borderRadius: `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`,
  height: "100%",
}));

const PhaseInContent = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  paddingBottom: 0,
  background: theme.palette.surface.main,
}));

const PackagingAreaContent = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  flex: 1,
}));

const PhaseInContentPreferred = styled("div")(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

interface Props {
  productDescriptor: ProductDescriptor<PdpPageProductData>;
  hasPreferredProducts: boolean;

  preferredProductCodes: string[];
  changeTab: (tabId: string) => void;
}

/*
 * Extended product details page summary (starting from breadcrumbs, finishing before tabs).
 * Originally for Distributors only, since SCHAEFF-24168 it's availabile for all Customers (minus Salesmen).
 */
export const ExtendedPdpSummary = ({ productDescriptor, preferredProductCodes, changeTab }: Props) => {
  const { t } = useTranslation();
  const buyboxRecoEnabled = useKameleoonFlag(KAMELEOON_FLAGS.buyboxReco);

  const [selectedProducts, setSelectedProducts] = useAddServiceProductsDataState();

  const reco_place = usePurchasePlace();
  const displayMMDAssistant = useDisplayMMDAssistant();

  const salesData = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor }), [productDescriptor]);
  const hasSales = salesData.data?.hasSales;

  const formProps = useFormContext<AddToCartFormDraft>();
  const { main, initialPackCodeResolved, prepareSubmitData } = useAddToCartDerived();
  const quantity = formProps.watch("quantity");
  const packCode = formProps.watch("selectedPackCode");
  const priceReference = formProps.watch("priceReference");
  const pack: PackagingInformationEntry | null = main.selectedPack;
  const selectedPackaging = main.selectedPackaging;
  const previewQuantity = Number(quantity);
  const phaseOut2Enabled = isPhaseOut2Enabled();

  const saleForPack: PdpSaleData | undefined = salesData.data?.salePerPack[packCode];
  const saleActive = !!saleForPack && formProps.watch("salePriceEnabled");
  const { variantLoading } = useVariantContext();

  const tariffSurcharge = isTariffSurchargeEnabled() && (main.netPrice?.tariffSurcharge ?? false);

  const optionalParts = formProps.watch("optionalParts") as Record<string, OptionalPartDraft>;

  const restrictionQuery = usePackageRestrictionContext() ?? "";

  const selectedPackageCode = formProps.watch("selectedPackCode");
  const onSelectPackage = (packCode: string) => formProps.setValue("selectedPackCode", packCode);
  const packagingOptionsState = usePackagingOptions({
    descriptor: productDescriptor,
    priceReference,
    salePerPack: salesData.data?.salePerPack ?? {},
    restrictionPackCode: restrictionQuery,
    t,
  });

  const effectivePackOption = packagingOptionsState.options.find((o) => o.packCode === packCode);
  const packSize =
    selectedPackaging?.packSize ??
    (phaseOut2Enabled && effectivePackOption ? effectivePackOption.packSize : pack?.packSize ?? 1);

  const qtyInCart = useSelector(selectAmountInCart({ product: productDescriptor, packCode }));
  const isCartLoading = useSelector(selectIsCartAmountsIsLoading);
  const {
    flags: { showBetterDeal, isPackageSizeInvalid, MOQHelperLabel },
  } = usePdpSummaryLogic({
    product: productDescriptor,
    qtyInCart,
    quantity,
    packCode,
    packSize,
    isOnSale: hasSales,
  });

  const phaseInLogic = usePhaseInOutLogic({ product: productDescriptor, preferredProductCodes });

  const product = productDescriptor.base;

  const isHousingKit = isKITProduct(product);

  const onAddToCartClick = async () => {
    const formValues = formProps.getValues();

    const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();
    const context = {
      page: reco_place,
      recommendationPlace,
      recommendationType,
    };

    if (isHousingKit) {
      if (selectedProducts.length > 0) {
        // Jan says this case cannot happen.
        throw new Error("Not implemented on purpose");
      }
      AddToCartService.openAddToCartWizard(
        {
          restrictionQuery,
          productCode: product.code,
          initialData: formValues,
          source: "pdp",
        },
        context,
      );
    } else {
      if (selectedProducts.length > 0) {
        const serviceItems = await addToCartItemsFromBasicItems(selectedProducts, { restrictionQuery, context });
        const submitData = prepareSubmitData();
        await addToCart({
          items: [
            addToCartItemFromSubmitData({
              submitData,
              productCode: product.code,
              restrictionQuery,
              context,
            }),
            ...serviceItems,
          ],
          page: context.page,
          source: "pdp",
          recommendationPlace: context.recommendationPlace,
          recommendationType: context.recommendationType,
        });
      } else {
        const submitData = prepareSubmitData();
        await addToCart({
          items: [
            addToCartItemFromSubmitData({
              submitData,
              productCode: product.code,
              restrictionQuery,
              context,
            }),
          ],
          page: context.page,
          source: "pdp",
          recommendationPlace: context.recommendationPlace,
          recommendationType: context.recommendationType,
        });
      }
    }
  };

  const isSubscription = !productDescriptor.base.quantityVisible;

  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({
    productInput: product,
    selectedOptionalParts: optionalParts,
    initialPriceReference: priceReference,
    initialQuantity: quantity,
  });

  const onWishlistBtnClick = () => {
    trackPdpWishlistClick(productDescriptor.base.designation, 1);
    openAddToWishlist();
  };

  const userCanAddToCart = getCanUserAddToCart(productDescriptor, pack);

  const hasSurcharge = (main.netPrice?.surcharge ?? 0) > 0;
  const isDistributorPricingTableVisible =
    !isSubscription && !isProductPMA(productDescriptor) && !isKITKITProduct(productDescriptor.base) && !saleActive;

  const isKITKIT = isKITKITProduct(productDescriptor.base);
  const isPackagingLoading =
    !initialPackCodeResolved ||
    productDescriptor.isAvailabilityLoading ||
    salesData.status === "loading" ||
    packagingOptionsState.status === "loading";
  const hasPackagingAvailability = Boolean(productDescriptor.availability);

  const exceedsSalesStock = !!saleForPack && quantity > saleForPack.salesStock;
  const minOrderQuantity = findMinOrderQuantity(productDescriptor, packCode);

  const isMOQCheckEnabled = isMinOrderQuantityRestrictionEnabled() && !hasSales;

  const phaseInBanners = [
    phaseInLogic.successorBanner === "packages" && phaseInLogic.successorBannerVariant === "basic" && (
      <SuccessorNotification variant={"basic"} successor={product.successorProduct!} />
    ),
    phaseInLogic.successorBanner === "packages" && phaseInLogic.successorBannerVariant === "extended" && (
      <SuccessorNotification successor={product.successorProduct!} variant={"extended"} discontinued={product} />
    ),
    phaseInLogic.alternativeBanner && <AlternativeNotification data={phaseInLogic.alternativeBanner} />,
  ];

  const hasPhaseInBanner = phaseInBanners.some(Boolean);

  const quantityVisible = product.quantityVisible && !(product.externalProduct && isProductPMA(productDescriptor));
  const isExternalProduct = product.externalProduct;
  const { selection } = usePriceReferenceSelection();
  const isPriceRefVisible = shouldShowPriceReference(isExternalProduct, productDescriptor, selection);

  const mmdCmnData = usePdpCmnLogic();

  const minQtyValue = isMOQCheckEnabled ? getDefaultQuantity(packSize, minOrderQuantity) : packSize;

  const simulateEntries = useMemo(
    () =>
      phaseOut2Enabled && packCode && Number.isFinite(previewQuantity) && previewQuantity > 0
        ? [
            {
              productCode: product.code,
              packageCode: packCode,
              packageSize: packSize,
              quantity: previewQuantity,
            },
          ]
        : [],
    [packCode, phaseOut2Enabled, previewQuantity, product.code],
  );

  const {
    data: previewData,
    quantityAdjusted,
    isLoading: isPreviewLoading,
  } = useSimulateCartPreview({
    entries: simulateEntries,
    enabled: simulateEntries.length > 0,
  });

  let qtyHelperLabel: ReactNode | undefined =
    isMOQCheckEnabled && minOrderQuantity > 1 ? t("page.moq.helperText.moqNotMet", { minOrderQuantity }) : undefined;
  let qtyIsError = false;
  if (phaseOut2Enabled && isPreviewLoading) {
    // noop
  } else if (phaseOut2Enabled && quantityAdjusted) {
    const adjustedQty = previewData?.products.flatMap((p) => p.lineItems).reduce((a, b) => a + b.quantity, 0) ?? 0;
    qtyHelperLabel = (
      <Box component={"span"} sx={{ color: "warning.main" }}>
        <Trans i18nKey={"product.phaseout.qtyAdjusted.future"} values={{ amount: adjustedQty }}>
          Your quantity will be adjusted to <b>{adjustedQty}</b>
        </Trans>
      </Box>
    );
  } else if (isPackageSizeInvalid) {
    qtyHelperLabel = isMOQCheckEnabled ? MOQHelperLabel : t("addToCartPopup.mismatchedQty");
    qtyIsError = isPackageSizeInvalid;
  } else if (phaseInLogic && phaseInLogic.purchaseLogicChange && pack) {
    const totalStockOfPackage = getTotalStock(pack);
    if (quantity > totalStockOfPackage) {
      if (phaseInLogic.purchaseLogicChange && phaseInLogic.purchaseLogicChange.packCodes.includes(pack.packCode)) {
        if (phaseInLogic.purchaseLogicChange.type === "warn") {
          qtyHelperLabel = t(PHASE_IN_BUY_WARN_KEY, { quantity: totalStockOfPackage });
        } else {
          // phaseInLogic.purchaseLogicChange === "error"
          qtyHelperLabel = t(PHASE_IN_BUY_ERROR_KEY, { quantity: totalStockOfPackage });
          qtyIsError = true;
        }
      }
    }
  } else if (saleActive && exceedsSalesStock) {
    qtyHelperLabel = t("feature.salesCampaign.exceedsQty");
  }

  const addToCartDisabled =
    window.SCHA.config.disableAllWS ||
    !userCanAddToCart ||
    qtyIsError ||
    productDescriptor.isLoading ||
    productDescriptor.isError;

  const buttons = renderButtons(productDescriptor, {
    atc: {
      disabled: addToCartDisabled || variantLoading,
      onClick: onAddToCartClick,
      forceChangeToQuote: phaseInLogic.changeATCLabelToQuote,
    },
    wishlist: {
      disabled: variantLoading,
      onClick: onWishlistBtnClick,
    },
    configurator: {
      disabled: variantLoading,
      onClick: () => changeTab(TabId.CONFIGURATOR),
    },
    atcFromSP: {
      disabled: variantLoading,
      quantity,
    },
  });

  const col1 = (
    <Stack gap={2}>
      <Box sx={{ position: "relative" }}>
        {hasSales && <SaleTag sx={{ position: "absolute", top: 0, left: "var(--flex-grid-side-padding)" }} />}
        <Product.PrimaryImageWithLogosV2 logoWidth={66} gap={24} size={"1fr"} />
      </Box>

      {isHousingKit && <HousingMessages.NotFinal />}

      <ProductDetailsSection />
    </Stack>
  );

  const col2 = (
    <PackagingArea>
      {hasPhaseInBanner && <PhaseInContent>{phaseInBanners}</PhaseInContent>}
      <PackagingAreaContent>
        {phaseInLogic.discontinuedBanner === "product" && !phaseInLogic.alternativeBanner && (
          <DiscontinuationNotification
            className={"scha__pdp__phase-out__successor__package"}
            variant={"product"}
            discontinued={product}
          />
        )}
        {isKITKIT && (
          <Stack gap={2}>
            <HousingMessages.PackagingDisclaimer />
            <Alert severity={"info"}>{t("addToCartPopup.disclaimer")}</Alert>
          </Stack>
        )}
        {!isKITKIT && (
          <>
            {isPackagingLoading && (
              <CenterContents className={"mb-30 w-100"}>
                <CircleSpinner />
              </CenterContents>
            )}

            <div className={"d-flex flex-justify-between mb-3 flex-align-center"} hidden={!hasPackagingAvailability}>
              <span className={"title-medium"}>{t("addToCartPopup.packagingOption")}</span>
            </div>

            {hasPackagingAvailability && !isPackagingLoading && packagingOptionsState.status === "loaded" && (
              <PackagingSelection
                value={selectedPackageCode}
                onChange={onSelectPackage}
                options={packagingOptionsState.options}
              />
            )}
            <div className="scha__pdp__notifications">
              {isProductPMA(productDescriptor) && <InfoProductMessagePMA />}
              <DisclaimerCountryOrigin
                // disabled={getCountriesList({ product: productDescriptor.base }).length === 0}
                disabled={getCountriesListFromFifteendigit({ descriptor: productDescriptor }).length === 0}
              />
            </div>
          </>
        )}
      </PackagingAreaContent>

      {phaseInLogic.preferredProductCodes && (
        <ProductContext value={product}>
          <PhaseInContentPreferred>
            <PreferredProducts data={phaseInLogic.preferredProductCodes} />
          </PhaseInContentPreferred>
        </ProductContext>
      )}
    </PackagingArea>
  );

  const col3 = (
    <Box sx={{ p: 2, position: "relative" }}>
      {(productDescriptor.isLoading || isCartLoading) && <CircleSpinner className={"mb-30"} />}

      {saleActive && <SaleEndIndicatorStyled endDate={saleForPack?.endDate} />}

      <Stack gap={2} hidden={!productDescriptor.isLoaded}>
        <PdpPriceBlock
          descriptor={productDescriptor}
          packCode={packCode}
          quantity={quantity ?? 1}
          priceReference={priceReference}
        />

        {product.isOptime && <OptimeSolutionsMessage />}

        {hasSales && (
          <Controller
            name={"salePriceEnabled"}
            render={({ field: { value, onChange } }) => (
              <CheckboxWithLabel checked={value} onCheck={onChange} hidden={!saleForPack || !!priceReference}>
                {t("feature.salesCampaign.specialDealPrice")}
              </CheckboxWithLabel>
            )}
          />
        )}

        <VariantSelect />

        {isPriceRefVisible && (
          <PriceReferenceParam
            data-test-id={"pdp-price-reference"}
            priceReference={priceReference}
            externalLabel={true}
            product={product}
            editable={canSeePriceRefColumn(selection)}
            disabled={variantLoading || saleActive}
            onChange={(value) => formProps.setValue("priceReference", value ?? "")}
          />
        )}

        {quantityVisible && (
          <QuantityInput
            data-test-id={"pdp-quantity"}
            value={quantity}
            onValueChange={(value) => formProps.setValue("quantity", value, { shouldTouch: true, shouldDirty: true })}
            min={minQtyValue}
            step={packSize}
            error={qtyIsError}
            helperLabel={qtyHelperLabel}
            max={MAX_QUANTITY}
            variant={"compact"}
            disabled={variantLoading}
          />
        )}

        <InfoOrderHandlingUnitQuantity
          preferredPackQuantity={pack?.preferredPackQuantity ?? null}
          packSize={packSize}
          quantity={quantity}
        />

        {showBetterDeal && <Alert severity="info">{t("addProduct.scalingDeal")}</Alert>}

        {phaseInLogic.showAdditionalSuccessorLink && (
          <Link href={resolveUrl(product.successorProduct!.url)}>
            {t("page.pdp.phaseinout.goToSuccessorLink", {
              designation: product.successorProduct!.designation,
            })}
          </Link>
        )}

        {isDistributorPricingTableVisible && (
          <DistributorPriceTable
            product={productDescriptor}
            selectedPackageCode={selectedPackageCode}
            quantity={quantity}
          />
        )}

        {buyboxRecoEnabled && (
          <AddServiceProductsSection
            productCode={productDescriptor.base.code}
            selectedProducts={selectedProducts}
            onSelectedProductChange={setSelectedProducts}
          />
        )}

        {ProductAddPreview.isVisible(product.designation, packCode, previewData) && (
          <ProductAddPreview data={previewData} />
        )}

        <Stack gap={1}>{buttons}</Stack>

        {!tariffSurcharge && <IncludesSurchargeDisclaimer hasSurcharge={hasSurcharge} tariffSurcharge={false} />}
      </Stack>
    </Box>
  );

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <PhaseInOutLogicProvider resultData={phaseInLogic}>
        <SalesDataProvider value={salesData.data}>
          <Grid container columns={12} spacing={3}>
            <Grid size={3} sx={{ position: "relative" }}>
              {col1}
            </Grid>
            <Grid size={9}>
              <Stack gap={2}>
                {displayMMDAssistant && (
                  <MMDAssistant
                    marketDesignation={product.marketDesignation}
                    initialData={{
                      quantity: quantity,
                      priceReference: priceReference,
                      ...mmdCmnData,
                    }}
                  />
                )}

                <Grid container columns={9} component={Card}>
                  <Grid size={5}>{col2}</Grid>
                  <Grid size={4}>{col3}</Grid>
                </Grid>
              </Stack>
            </Grid>
          </Grid>
          {addToWishlistModal}
        </SalesDataProvider>
      </PhaseInOutLogicProvider>
    </ProductDescriptorContext>
  );
};

export const getCanUserAddToCart = (
  product: ProductDescriptor,
  packaging: PackagingInformationEntry | undefined | null,
) => {
  if (!packaging || !product.isLoaded) {
    return false;
  }

  const packCode = packaging.packCode;
  const priceInfo = getPriceInfo({ product, packCode });
  const availabilityInfo = product.availability;

  const isDataAvailable = (priceInfo?.hasPrice ?? false) && (availabilityInfo?.hasPackaging ?? false);

  return isDataAvailable;
};

const SaleEndIndicatorStyled = styled(SaleEndIndicator.V2)({
  position: "absolute",
  top: 4,
  right: 4,
});
